import React, { useState, useEffect } from "react";
import { Box, Tooltip, Badge, Drawer, Typography, IconButton, Chip, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DevicesIcon from "@mui/icons-material/Devices";
import ScienceIcon from "@mui/icons-material/Science";
import WarningIcon from "@mui/icons-material/Warning";
import { useTranslation } from "react-i18next";
import { useSystemId } from "../../hooks/useReduxSelectors";
import AlarmItem from "../alarms/alarmItem/AlarmItem";

const ICON_MAP = {
  Alarms: NotificationsIcon,
  Devices: DevicesIcon,
  Samples: ScienceIcon,
  Warnings: WarningIcon,
};

const NAV_WIDTH = 64;
const DRAWER_WIDTH = 300;

const navItemSx = (isSelected) => ({
  width: NAV_WIDTH,
  height: NAV_WIDTH,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "1px solid",
  borderColor: isSelected ? "secondary.main" : "divider",
  bgcolor: isSelected ? "action.selected" : "transparent",
  color: isSelected ? "secondary.main" : "text.primary",
  "&:hover": { bgcolor: "action.hover" },
  position: "relative",
});

const RightSideNav = ({
  menuitems,
  categoryAlarms,
  onDeleteAlarms,
  onGetAlarmsByCategory,
  isCompact,
  isMobile,
  containerRef,
  rightDrawerOpen,
  onCloseRightDrawer,
  serviceModeActive,
  isManualMode,
  onAutoManualToggle,
}) => {
  const { t } = useTranslation();
  const systemId = useSystemId();
  const theme = useTheme();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Mobile/compact drawer: default to showing all active alarms when opened with no chip selected.
  useEffect(() => {
    if (isCompact && rightDrawerOpen && !selectedCategory) {
      setSelectedCategory("alarms");
      onGetAlarmsByCategory("alarms");
    }
  }, [isCompact, rightDrawerOpen]);

  const handleCategoryClick = (label) => {
    const key = label.toLowerCase();
    if (selectedCategory === key && drawerOpen) {
      setDrawerOpen(false);
      setSelectedCategory("");
    } else {
      setSelectedCategory(key);
      onGetAlarmsByCategory(key);
      setDrawerOpen(true);
    }
  };

  const handleCategoryClickCompact = (label) => {
    const key = label.toLowerCase();
    if (selectedCategory === key) {
      // Deselecting a chip falls back to showing all active alarms.
      setSelectedCategory("alarms");
      onGetAlarmsByCategory("alarms");
    } else {
      setSelectedCategory(key);
      onGetAlarmsByCategory(key);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedCategory("");
  };

  const handleCompactClose = () => {
    setSelectedCategory("");
    if (onCloseRightDrawer) onCloseRightDrawer();
  };

  const menuList = menuitems.menuList || [];

  const renderNavItem = (menu) => {
    const isCategory = menu.isCategory;
    const isSelected = menu.label.toLowerCase() === selectedCategory;
    const hasCounts = menu.counts !== undefined && menu.counts > 0;

    return (
      <Tooltip key={menu.label} title={t(menu.label)} placement="left">
        <Box
          sx={{
            ...navItemSx(isSelected),
            ...(isCategory && {
              cursor: "default",
              "&:hover": { bgcolor: "transparent" },
            }),
          }}
          onClick={isCategory ? undefined : () => handleCategoryClick(menu.label)}
        >
          <Box sx={{ fontSize: 24, lineHeight: 1, display: "flex", alignItems: "center" }}>
            {(() => {
              const Icon = ICON_MAP[menu.label];
              return Icon ? <Icon fontSize="large" sx={{ color: isSelected ? "secondary.main" : "text.primary" }} /> : null;
            })()}
          </Box>
          {hasCounts && (
            <Badge
              badgeContent={menu.counts}
              color="warning"
              sx={{ position: "absolute", top: 8, right: 8 }}
            />
          )}
          {isCategory && (menu.isActiveAlarms || menu.isActiveWarnings) && (
            <Box
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: menu.isActiveAlarms ? "error.main" : "warning.main",
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  const sortedAlarms = categoryAlarms
    ? [...categoryAlarms].sort((a, b) => a.alarmId.localeCompare(b.alarmId))
    : [];

  // Compact / mobile layout — full temporary drawer from right
  if (isCompact) {
    return (
      <Drawer
        anchor="right"
        open={rightDrawerOpen}
        onClose={handleCompactClose}
        ModalProps={{ disablePortal: true, keepMounted: true, disableAutoFocus: true, disableRestoreFocus: true }}
        sx={{
          position: "absolute",
          "& .MuiDrawer-paper": { position: "absolute", width: 300 },
          "& .MuiBackdrop-root": { position: "absolute" },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("Alarms")}</Typography>
            <Box>
              <Tooltip title={t("Clear inactive")}>
                <IconButton size="small" onClick={(e) => onDeleteAlarms(null, e)}>
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleCompactClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Category filter chips */}
          <Box sx={{ display: "flex", gap: 0.5, p: 1, borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
            {menuList.filter(m => !m.isCategory).map(menu => {
              const key = menu.label.toLowerCase();
              const Icon = ICON_MAP[menu.label];
              const isSelected = selectedCategory === key;
              return (
                <Chip
                  key={menu.label}
                  icon={Icon ? <Icon sx={{ fontSize: 16 }} /> : undefined}
                  label={`${t(menu.label)} (${menu.counts || 0})`}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "secondary" : "default"}
                  onClick={() => handleCategoryClickCompact(menu.label)}
                />
              );
            })}
          </Box>

          {/* Alarm list */}
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            {sortedAlarms.length > 0 ? (
              sortedAlarms.map((alarm, index) => (
                <AlarmItem
                  key={`alarm_${alarm.alarmId}_${alarm.hwId}`}
                  alarm={alarm}
                  order={index}
                  onDeleteAlarm={onDeleteAlarms}
                />
              ))
            ) : (
              <Typography sx={{ p: 2, color: "text.secondary" }}>{t("No alarms")}</Typography>
            )}
          </Box>
        </Box>
      </Drawer>
    );
  }

  // Desktop layout — nav rail + persistent drawer
  return (
    <>
      <Box
        component="nav"
        sx={{
          width: NAV_WIDTH,
          minWidth: NAV_WIDTH,
          height: "100%",
          bgcolor: "inherit",
          display: "flex",
          flexDirection: "column",
          zIndex: 10,
          boxShadow: -2,
          borderLeft: '2px solid',
          borderColor: theme.palette.surface.mutedBorder,
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {menuList.map(renderNavItem)}
        </Box>
        {serviceModeActive && (
          <Box sx={{ p: 0.5, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "center" }}>
            <Box
              onClick={onAutoManualToggle}
              sx={{
                width: "100%",
                cursor: "pointer",
                borderRadius: 1,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                "&:hover": { borderColor: theme.palette.surface.mutedBorder },
              }}
            >
              <Box
                sx={{
                  height: isManualMode ? 25 : 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: isManualMode ? "text.primary" : "common.white",
                  bgcolor: isManualMode ? "transparent" : "success.main",
                  transition: "all 0.2s",
                }}
              >
                {t("Auto")}
              </Box>
              <Box
                sx={{
                  height: isManualMode ? 50 : 25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: isManualMode ? "common.white" : "text.primary",
                  bgcolor: isManualMode ? "warning.main" : "transparent",
                  transition: "all 0.2s",
                }}
              >
                {t("Manual")}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        variant="persistent"
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            right: NAV_WIDTH,
            boxShadow: 3,
            position: "absolute",
            top: 0,
            bottom: 0,
            height: "100%",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
            {t(selectedCategory)}
          </Typography>
          <Box>
            <Tooltip title={t("Clear inactive")}>
              <IconButton size="small" onClick={(e) => onDeleteAlarms(null, e)}>
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={handleDrawerClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {sortedAlarms.length > 0 ? (
            sortedAlarms.map((alarm, index) => (
              <AlarmItem
                key={`alarm_${alarm.alarmId}_${alarm.hwId}`}
                alarm={alarm}
                order={index}
                onDeleteAlarm={onDeleteAlarms}
              />
            ))
          ) : (
            <Typography sx={{ p: 2, color: "text.secondary" }}>
              {t("No alarms")}
            </Typography>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default RightSideNav;
